import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/lib/hooks/useDebounce';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Skeleton } from '@/components/ui/skeleton';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import type { Client, ClientFilters, ClientType } from '../types/client.types';

interface ClientsDataTableProps {
  data: Client[];
  columns: ColumnDef<Client>[];
  isLoading: boolean;
  filters: ClientFilters;
  updateFilter: <K extends keyof ClientFilters>(key: K, value: ClientFilters[K]) => void;
  updateFilters?: (updates: Partial<ClientFilters>) => void;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

export function ClientsDataTable({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  updateFilters,
  onClearFilters,
  controlsContainer,
  tableZoom: tableZoomProp,
}: ClientsDataTableProps) {
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('clients');
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Debounced search
  const [searchValue, setSearchValue] = useState(filters.s ?? '');
  const debouncedSearch = useDebounce(searchValue, 300);
  const isFirstRender = useRef(true);

  // Update filter when debounced search changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const trimmed = debouncedSearch.trim();
    const newValue = trimmed.length >= 2 ? trimmed : undefined;
    // Only update if value actually changed to avoid resetting page
    if (newValue !== filters.s) {
      updateFilter('s', newValue);
    }
  }, [debouncedSearch]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.s) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    setSearchValue('');
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Client Type Toggle */}
      <ToggleGroup
        type="single"
        size="sm"
        className="justify-start gap-1"
        value={filters.type || 'none'}
        onValueChange={(item) =>
          updateFilter('type', item === 'none' ? undefined : (item as ClientType))
        }
      >
        <ToggleGroupItem
          value="none"
          aria-label="All"
          className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          {t('all')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="PERSON"
          aria-label="PERSON"
          className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          <img src="/images/person.svg" alt="" className="inline mr-1.5 h-4 w-4 data-[state=on]:brightness-0 data-[state=on]:invert" />
          {t('naturalPerson')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="COMPANY"
          aria-label="COMPANY"
          className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          <img src="/images/company.svg" alt="" className="inline mr-1.5 h-4 w-4 data-[state=on]:brightness-0 data-[state=on]:invert" />
          {t('legalEntity')}
        </ToggleGroupItem>
      </ToggleGroup>

      {/* Table Actions Bar */}
      <div className="flex items-center justify-end gap-2 px-2">
        {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
        <TableHeaderControls container={controlsContainer}>
          <ColumnVisibility table={table} />
          <FilterToggleButton
            activeFilterCount={activeFilterCount}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((v) => !v)}
          />
        </TableHeaderControls>
      </div>

      {/* Filters panel */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={1}
      >
        {/* Search */}
        <FilterField label={t('search')}>
          <div className="relative">
            <Input
              placeholder={t('search')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pr-10 h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
            />
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </FilterField>
      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="hover:bg-[#f8fafc]">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('clientNotFounded')}</h3>
                    <p className="text-[#64748b]">{t('clientNotFoundedDesc')}</p>
                    {hasAccess('client', 'create') && (
                      <div className="flex gap-3 justify-center mt-4">
                        <Button onClick={() => navigate('actionPerson')}>
                          {t('newPerson')}
                        </Button>
                        <Button onClick={() => navigate('actionCompany')}>
                          {t('newCompany')}
                        </Button>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
